<template>
  <component
    :is="tag"
    :class="{
      ...buttonClasses,
      ...classes,
    }"
    v-bind="$attrs"
    :aria-label="labelText"
  >
    <!-- @i Icon prop used to load icon below -->

    <pdl-icon v-if="showIcon" :name="icon" :size="iconSize" />

    <span v-if="standalone">
      <!-- @slot Use for the link's content. -->

      <slot />
    </span>

    <template v-else>
      <!-- @slot Use for the link's content. -->

      <slot />
    </template>
  </component>
</template>

<script>
import {PdlIcon} from '@pedal/pdl-icon';
import {PdlButtonMixin} from '@pedal/pdl-shared-libraries';

export default {
  name: 'PdlLink',
  components: {PdlIcon},
  mixins: [PdlButtonMixin],
  inheritAttrs: false,
  props: {
    /**
     * Applies button styling to the link.
     */
    button: {
      type: Boolean,
      default: false,
    },
    /**
     * Applies standalone styling to the link.
     */
    standalone: {
      type: Boolean,
      default: false,
    },
    /**
     * Standalone heading link styling (must be paired with `standalone` prop).
     */
    isHeading: {
      type: Boolean,
      default: false,
    },
    /**
     * Applies disabled styling to the link.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * X-Small size standalone link.
     */
    xsmall: {
      type: Boolean,
      default: false,
    },
    /**
     * Small size standalone link.
     */
    small: {
      type: Boolean,
      default: false,
    },
    /**
     * Medium size standalone link.
     */
    medium: {
      type: Boolean,
      default: false,
    },
    /**
     * Text used to describe the context of an icon-only link. A label is required when using an icon-only link,
     * as there is no pairing of visible text with the icon. The text will be added to an aria-label on the anchor element.
     */
    label: {
      type: String,
      default: undefined,
    },
  },
  computed: {
    classes() {
      return {
        'pdl-link': !this.button,
        'pdl-link--standalone': !this.button && this.standalone,
        'pdl-link--standalone-heading': !this.button && this.standalone && this.isHeading,
        'pdl-link--icon-right': !this.button && this.standalone && this.iconRight,
        'pdl-link--disabled': !this.button && this.standalone && this.disabled,
        'text-xs': !this.button && this.standalone && this.xsmall,
        'text-sm': !this.button && this.standalone && this.small,
        'text-md': !this.button && this.standalone && this.medium,
      };
    },
    tag() {
      return this.disabled ? 'span' : 'a';
    },
    showIcon() {
      return (this.button || this.standalone) && this.icon;
    },
    hasSize() {
      return this.xsmall || this.small || this.medium;
    },
    iconSize() {
      if (this.xsmall) {
        return '14';
      } else if (this.small) {
        return '18';
      }
      return '24';
    },
    labelText() {
      return this.iconOnly ? this.label : undefined;
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps();
      },
    },
  },
  methods: {
    validateProps() {
      if (this.hasSize && !this.standalone && !this.button) {
        // eslint-disable-next-line no-console
        console.warn('Size props can only be used with standalone or button props.');
      }

      if (!this.standalone && this.isHeading) {
        // eslint-disable-next-line no-console
        console.warn('Only the standalone prop can be used with isHeading prop.');
      }
    },
  },
};
</script>
<style lang="scss">
@import 'Link.scss';
@import '@pedal/pdl-button/src/Button.scss';
@import '@pedal/pdl-button/src/ButtonGroup.scss';
</style>
